import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FocusableContainer from '../FocusableContainer'
import Search from '../Search'
import { SearchUsersIcon } from '../../../icons'
import { useOnClickOutside } from '../../../hooks/useOnClickOutside'
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect'
import { keyboardEvents } from '../../../utils/keyboardEvents'
import { getUsersList } from '../../../utils/requests/usersAPI'
import './user-search-input.scss'

const UserSearchInput = ({ initialSelectedKey, onChange, hasError, isDisabled, setShowSpinner, defaultQueryParams, localFilters, className = '' }) => {
  const { t } = useTranslation()
  const [isSelectMenuOpen, setIsSelectMenuOpen] = useState(false)
  const [selectedKey, setSelectedKey] = useState(initialSelectedKey)
  const [focusedOption, setFocusedOption] = useState(0)
  const [keyboardFocused, setKeyboardFocused] = useState(false)
  const [options, setOptions] = useState([])
  const [usersSearchParams, setUsersSearchParams] = useState(null)
  const ref = useRef()
  const optionsRefs = useRef([])

  useEffect(() => {
    if (initialSelectedKey !== selectedKey) {
      setSelectedKey(initialSelectedKey)
    }
  }, [initialSelectedKey])

  useEffect(() => {
    if (isSelectMenuOpen) {
      setFocusedOption(0)
    }

    if (!isSelectMenuOpen) {
      setKeyboardFocused(false)
      setOptions([])
    }
  }, [isSelectMenuOpen])

  useEffect(() => {
    setUsersSearchParams(initialSelectedKey)
  }, [initialSelectedKey])

  useEffect(() => {
    if (initialSelectedKey !== usersSearchParams) {
      setIsSelectMenuOpen(usersSearchParams || usersSearchParams === '' ? true : false)
    }
  }, [usersSearchParams])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        if (usersSearchParams !== initialSelectedKey) {
          handleLoadUsers(true)
        }
      },
      usersSearchParams ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [usersSearchParams])

  useEffect(() => {
    if (optionsRefs.current[focusedOption]) {
      optionsRefs.current[focusedOption].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [focusedOption])

  useOnClickOutside(ref, () => {
    setIsSelectMenuOpen(false)
  })

  const handleLoadUsers = () => {
    let queryParams = {
      ...(defaultQueryParams || {}),
    }

    if (usersSearchParams?.length > 0) {
      queryParams = { ...queryParams, query: usersSearchParams }
    }

    getUsersList(queryParams)
      .then((response) => {
        if (response?.data?.length > 0) {
          let users = response.data

          if (localFilters.account) {
            users = users.filter((user) => user.account?.name === localFilters.account)
          }

          if (localFilters.branchName || localFilters.branchName === null) {
            users = users.filter((user) => user.branchName === localFilters.branchName)
          }

          const mappedUsersToOptions = users.map((user) => ({
            key: user.email,
            value: user.email,
            isDisabled: user.status !== 'activated' || false,
            additionalContent: null,
          }))

          const uniqueMappedUsersToOptions = mappedUsersToOptions.filter(
            (option, index, self) => index === self.findIndex((t) => t.key.toLowerCase() === option.key.toLowerCase()),
          )
          setOptions(uniqueMappedUsersToOptions)

          return
        }

        setOptions([])
      })
      .catch(() => {
        setOptions([])
      })
      .finally(() => {
        setShowSpinner(false)
      })
  }

  const handleUsersSearch = (value) => {
    if (value?.length > 2) {
      setShowSpinner(true)
      if (initialSelectedKey !== value) {
        setUsersSearchParams(value)
      }
    } else if (value?.length === 0 && usersSearchParams?.length > 0) {
      setShowSpinner(true)
      setUsersSearchParams(value)
    }
  }

  const handleOptionSelection = (option) => {
    if (option.isDisabled) {
      return
    }

    setSelectedKey(option.key)
    setIsSelectMenuOpen(false)

    if (onChange && option.key !== selectedKey) {
      onChange(option)
    }
  }

  const handleKeyDown = (event) => {
    if (isDisabled) {
      return
    }

    if (event.key === keyboardEvents.ENTER) {
      setKeyboardFocused(true)

      if (!isSelectMenuOpen) {
        setIsSelectMenuOpen(true)
        return
      }

      const option = options[focusedOption]
      if (!option.isDisabled) {
        handleOptionSelection(option)
      }
    }

    if (event.key === keyboardEvents.ESC) {
      setIsSelectMenuOpen(false)
    }

    if (event.key === keyboardEvents.ARROW_DOWN) {
      event.stopPropagation()
      event.preventDefault()
      setKeyboardFocused(true)

      setFocusedOption((prev) => {
        return prev < options.length - 1 ? prev + 1 : 0
      })
    }

    if (event.key === keyboardEvents.ARROW_UP) {
      event.stopPropagation()
      event.preventDefault()
      setKeyboardFocused(true)

      setFocusedOption((prev) => {
        return prev > 0 ? prev - 1 : options.length - 1
      })
    }
  }

  return (
    <FocusableContainer
      ref={ref}
      className={`focusable-user-input-wrapper ${className} ${isDisabled ? 'focusable-user-input-disabled' : ''}`}
      onKeyDown={(e) => handleKeyDown(e)}
    >
      <div className={'display-input-wrapper' + (hasError ? ' error' : '')} onClick={() => setIsSelectMenuOpen(!isSelectMenuOpen)}>
        <Search onSearch={handleUsersSearch} searchText={usersSearchParams} placeholder={t('business-rules.search_by_name_email')} />
        <SearchUsersIcon />
      </div>
      {isSelectMenuOpen && (
        <div className="options-wrapper">
          {options.map((option, index) => (
            <div
              ref={(elem) => (optionsRefs.current[index] = elem)}
              key={option.key}
              className={
                'option' +
                (option.key?.toString()?.toLowerCase() === selectedKey?.toString()?.toLowerCase() ? ' selected-option' : '') +
                (option.isDisabled ? ' disabled-option' : '') +
                (keyboardFocused && focusedOption === index ? ' focused' : '')
              }
              onClick={() => handleOptionSelection(option)}
            >
              {option.value}
              {options?.find((o) => o.key === option.key)?.additionalContent}
            </div>
          ))}
          {options.length === 0 && <div className="no-options">{t('no_data_available')}</div>}
        </div>
      )}
    </FocusableContainer>
  )
}

export default UserSearchInput
